/**
 * HM-387 cluster login PIN: orchestratore Step 2.
 * Compone SelectedOperatorBar + PinDots + ErrorMessage + Keypad e gestisce
 * lo state del PIN inserito + chiamata POST /auth/login-pin via useMutation.
 *
 * Comportamento UX (mockup v2):
 * - tasto digit -> append, max 4 digit
 * - 4° digit -> setTimeout 200ms -> submit
 * - backspace -> remove ultimo
 * - clear -> reset
 * - errore 401 -> dots rossi + shake 400ms + error message visibile
 *                  -> dopo 500ms reset PIN, errore resta visibile fino a digit successivo
 * - submit pending -> keypad disabled (no race)
 */
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { type AuthUser, type Operator, loginPin } from '../../api/auth.js';
import { ApiError } from '../../api/client.js';
import { Keypad } from './Keypad.js';
import { PinDots } from './PinDots.js';
import { SelectedOperatorBar } from './SelectedOperatorBar.js';

export function PinKeypadStep({
  operator,
  hotelId,
  onBack,
  onSuccess,
}: {
  operator: Operator;
  hotelId?: string;
  onBack: () => void;
  onSuccess: (user: AuthUser) => void;
}) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Animazione shake: il flag stays true per 400ms quando submit fallisce.
  const [shake, setShake] = useState(false);
  const submitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guard anti doppio-submit: true dal momento in cui il 4° digit schedula
  // il submit, false solo quando il PIN torna sotto i 4 digit (reset post
  // errore / clear / success). Senza questo flag l'effect di auto-submit
  // (deps [pin, mutation]) ripartiva dopo il 401: isPending tornava false
  // mentre il PIN era ancora a 4 cifre (il reset arriva 500ms dopo) →
  // secondo POST /auth/login-pin identico con un solo inserimento.
  const submittedRef = useRef(false);

  const mutation = useMutation({
    mutationFn: (p: string) => loginPin({ username: operator.username, pin: p, hotelId }),
    onSuccess: (res) => {
      setPin('');
      setError(null);
      setShake(false);
      onSuccess(res.user);
    },
    onError: (err) => {
      let msg = 'Errore di connessione. Riprova.';
      if (err instanceof ApiError) {
        if (err.status === 401) msg = 'PIN non valido. Riprova.';
        else if (err.status === 429) msg = 'Troppi tentativi. Attendi un minuto.';
        else if (err.status === 400) msg = 'Formato PIN non valido.';
      }
      setError(msg);
      setShake(true);
      // Dopo 500ms reset PIN (lascia l'errore visibile per leggibilita').
      resetTimer.current = setTimeout(() => {
        setPin('');
        setShake(false);
      }, 500);
    },
  });

  // Auto-submit quando arrivo al 4° digit — UNA volta sola per inserimento.
  useEffect(() => {
    if (pin.length < 4) {
      // PIN modificato/resettato: riarma il guard e annulla un eventuale
      // submit gia' schedulato (backspace/clear nella finestra dei 200ms).
      submittedRef.current = false;
      if (submitTimer.current) {
        clearTimeout(submitTimer.current);
        submitTimer.current = null;
      }
      return;
    }
    if (submittedRef.current || mutation.isPending) return;
    submittedRef.current = true;
    submitTimer.current = setTimeout(() => {
      mutation.mutate(pin);
    }, 200);
  }, [pin, mutation]);

  // Cleanup tutti i timer al unmount.
  useEffect(() => {
    return () => {
      if (submitTimer.current) clearTimeout(submitTimer.current);
      if (resetTimer.current) clearTimeout(resetTimer.current);
    };
  }, []);

  const onDigit = (digit: string) => {
    if (mutation.isPending) return;
    setError(null);
    setShake(false);
    setPin((prev) => (prev.length >= 4 ? prev : prev + digit));
  };
  const onBackspace = () => {
    if (mutation.isPending) return;
    setError(null);
    setShake(false);
    setPin((prev) => prev.slice(0, -1));
  };
  const onClear = () => {
    if (mutation.isPending) return;
    setError(null);
    setShake(false);
    setPin('');
  };

  return (
    <div data-testid="pin-keypad-step">
      <SelectedOperatorBar operator={operator} onBack={onBack} />
      <div className="section-title">Inserisci PIN</div>
      <PinDots length={pin.length} error={shake} />
      <div className={`error-message${error ? ' shown' : ''}`} role="alert" aria-live="polite">
        {error ?? ' '}
      </div>
      <Keypad
        onDigit={onDigit}
        onBackspace={onBackspace}
        onClear={onClear}
        disabled={mutation.isPending}
      />
    </div>
  );
}
